import React, { useState } from "react";
import { ChevronDown, Lock, LockOpen } from "lucide-react";
import {
  formatDate,
  formatTime,
  handleStopPropagation,
} from "components/common/Utils";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Dropdown } from "react-bootstrap";
import { useDispatch } from "react-redux";
import { showAlert } from "store/reducers/alertReducer";
import DeleteConfirmationModal from "components/common/ConfirmationDialog";
import { spaceTypeOptions } from "components/constants/DropdownOptions";
import { deleteClosure } from "store/actions/closureSlice";
import ClosureImg from "assets/images/closure.webp";
import riyalGray from "assets/images/icons/riyalGray.svg";

const ClosureCard = ({ item, className = "", isLoading }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [selectedId, setSelectedId] = useState(null);
  const [showModal, setShowModal] = useState(false);

  const handleDelete = () => {
    const payload = {
      id: selectedId,
      status: "2", // 2 FOR DELETE
    };
    dispatch(deleteClosure(payload))
      .then((action) => {
        const { statusCode, success, message } = action?.payload;
        if (statusCode === 200 && success) {
          dispatch(showAlert({ text: message, type: "success" }));
          setShowModal(false);
          setSelectedId(null);
        } else {
          dispatch(showAlert({ text: message, type: "error" }));
        }
      })
      .catch((err) => {});
  };

  return (
    <>
      <div
        className={`inner_section c_p ${className}`}
        onClick={() => {
          navigate(`/management/ws-management/closure-details?id=${item._id}`);
        }}
      >
        <img
          src={item?.spaceId[0]?.imageUrls[0] || ClosureImg}
          className="workshop_img"
          alt=""
          width={230}
          height={168}
        />
        <div className="content w-100">
          <div className="left_section">
            {/* TOP ROW */}
            <div className="g_between align-items-start">
              <div className="w-100">
                <div className="g_between gap-5">
                  {item?.hasClosedEntireBranch ? (
                    <h2 className="fs_20 text-capitalize">
                      {t("Closed the entire branch")}
                    </h2>
                  ) : (
                    <h2 className="fs_20 text-capitalize">
                      {item?.spaceId?.map((item) => item.name).join(", ")}
                    </h2>
                  )}

                  <p
                    className={`${
                      item.type === 1 ? "available" : t("recurring")
                    } `}
                  >
                    {item.type === 1 ? t("One-Time") : t("Recurring")}{" "}
                  </p>
                </div>
                <p className="fs_14 text_dark_gray">
                  {item?.cityId.name}, {item?.branchId?.name}
                </p>
                <div className="g_between">
                  <div className="mt-2 d-flex gap-5">
                    <div className="d-flex align-items-start gap-2">
                      <Lock className="text_dark_gray" size={16} />
                      <div>
                        {item.type === 1 && (
                          <p className="fs_14 text_dark_gray">
                            {formatDate(item?.dateAndTimes?.startDate)}
                          </p>
                        )}
                        <p className="fs_14 text_dark_gray">
                          {formatTime(item?.dateAndTimes?.startTime)}
                        </p>
                      </div>
                    </div>
                    <div className="d-flex align-items-start gap-2">
                      <LockOpen className="text_dark_gray" size={16} />
                      <div>
                        {item.type === 1 && (
                          <p className="fs_14 text_dark_gray">
                            {formatDate(item?.dateAndTimes?.endDate)}
                          </p>
                        )}
                        <p className="fs_14 text_dark_gray">
                          {formatTime(item?.dateAndTimes?.endTime)}
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="" onClick={handleStopPropagation}>
                    <p className="text_primary fs_12">
                      {t("Created on:")}{" "}
                      <span className="text_dark_gray">
                        {formatDate(item?.createdAt)} {t("by")}
                      </span>{" "}
                      <Link
                        to={`/users/user-details?id=${item?.createdBy?._id}`}
                        className="text-decoration-underline"
                      >
                        {item?.createdBy?.name || ""}
                      </Link>
                    </p>
                    <p className="text_blue fs_12">
                      {t("Last Edit on:")}{" "}
                      <span className="text_dark_gray">
                        {" "}
                        {formatDate(item?.updatedAt)}
                      </span>{" "}
                      <span className="text_dark_gray">{t("by")}</span>{" "}
                      <Link
                        to={`/users/user-details?id=${item?.lastEditedBy?._id}`}
                      >
                        {item?.lastEditedBy?.name || "-"}
                      </Link>
                    </p>
                  </div>
                </div>
                {/* ACTIONS */}
                <div className="g_between align-items-end mt-2">
                  {item?.workShopDetails?.price && (
                    <p className="price_tag">
                      <img
                        src={riyalGray}
                        width={15}
                        height={15}
                        alt="riyal"
                        className=""
                      />
                      {item?.workShopDetails?.price}
                    </p>
                  )}
                  <Dropdown
                    className="custom_dropdown list_action_dropdown"
                    drop={"down"}
                    onClick={handleStopPropagation}
                  >
                    <Dropdown.Toggle>
                      <button className="list_edit_btn">
                        {t("Edit")} <ChevronDown width={18} height={18} />
                      </button>
                    </Dropdown.Toggle>

                    <Dropdown.Menu align="end">
                      <Dropdown.Item
                        href="#"
                        onClick={() => {
                          navigate(
                            `/management/ws-management/closure-details?id=${item?._id}`
                          );
                        }}
                      >
                        {t("View")}
                      </Dropdown.Item>
                      <Dropdown.Item
                        href="#"
                        onClick={() => {
                          navigate(
                            `/management/ws-management/update-closure?id=${item._id}`
                          );
                        }}
                      >
                        {t("Edit")}
                      </Dropdown.Item>
                      <Dropdown.Item
                        href="#"
                        className="text_danger"
                        onClick={() => {
                          setSelectedId(item._id);
                          setShowModal(true);
                        }}
                      >
                        {t("Delete")}
                      </Dropdown.Item>
                    </Dropdown.Menu>
                  </Dropdown>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <DeleteConfirmationModal
        show={showModal}
        onClose={() => {
          setShowModal(false);
          setSelectedId(null);
        }}
        onConfirm={handleDelete}
        message={t("Are you sure you want to delete this {{name}}?", {
          name: "Closure",
        })}
        isLoading={isLoading}
      />
    </>
  );
};

export default ClosureCard;
